import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, GitMerge, Database, Blocks, Shield, UserCheck, Workflow } from 'lucide-react';
const layers = [{
  title: 'Your Industry Workflow',
  description: 'The operational processes that drive your business.',
  icon: Workflow,
  color: 'text-gray-400',
  border: 'border-white/10',
  bg: 'bg-[#111]'
}, {
  title: 'LLM Orchestration',
  description: 'Multi-model reasoning selecting the right model for the right task.',
  icon: BrainCircuit,
  color: 'text-brand-orange',
  border: 'border-brand-orange/30',
  bg: 'bg-brand-orange/5'
}, {
  title: 'Workflow Engine',
  description: 'Automation that executes structured, multi-step actions.',
  icon: GitMerge,
  color: 'text-brand-orange',
  border: 'border-brand-orange/30',
  bg: 'bg-brand-orange/5'
}, {
  title: 'Memory & Context',
  description: 'Persistent understanding of past interactions, business rules, and operational state.',
  icon: Database,
  color: 'text-brand-orange',
  border: 'border-brand-orange/30',
  bg: 'bg-brand-orange/5'
}, {
  title: 'Integrations',
  description: 'Connection into your existing tools, systems, and data environments.',
  icon: Blocks,
  color: 'text-brand-orange',
  border: 'border-brand-orange/30',
  bg: 'bg-brand-orange/5'
}, {
  title: 'Security & Logic',
  description: 'Enterprise-grade controls, permissions, approvals, and rule enforcement.',
  icon: Shield,
  color: 'text-brand-orange',
  border: 'border-brand-orange/30',
  bg: 'bg-brand-orange/5'
}, {
  title: 'Human Handoff',
  description: 'Seamless escalation to human operators with full context.',
  icon: UserCheck,
  color: 'text-gray-400',
  border: 'border-white/10',
  bg: 'bg-[#111]'
}];
export function HowItWorks() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  return <section id="platform" className="py-32 bg-brand-black relative z-10" data-id="element-328">
      <div className="max-w-4xl mx-auto px-6" data-id="element-329">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-24" data-id="element-330">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6 tracking-tight leading-[1.0] text-balance" data-id="element-331">
            The Intelligence Stack.
          </h2>
          <p className="font-body text-lg text-gray-400 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-332">
            Aijentik provides the full architecture for vertical AI execution,
            from industry workflow understanding through to automation,
            integrations, security, and human handoff.
          </p>
        </motion.div>

        <div className="relative max-w-3xl mx-auto" data-id="element-333">
          {/* Animated vertical connecting line */}
          <div className="absolute left-[39px] top-10 bottom-10 w-px bg-white/5" data-id="element-334">
            <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-transparent via-brand-orange to-transparent animate-flow-down opacity-50" data-id="element-335" />

            {/* Pulsing nodes between layers */}
            {layers.slice(0, -1).map((_, i) => <motion.div key={`pulse-${i}`} className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-brand-orange" style={{
            top: `${(i + 1) * (100 / layers.length)}%`
          }} animate={{
            opacity: [0.2, 1, 0.2],
            scale: [1, 1.5, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.3
          }} data-id="element-336" />)}
          </div>

          <div className="space-y-6 relative z-10" data-id="element-337">
            {layers.map((layer, index) => {
            const isHovered = hoveredIndex === index;
            const isDimmed = hoveredIndex !== null && hoveredIndex !== index;
            return <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)} className={`relative flex items-center gap-8 transition-all duration-700 ease-premium cursor-default ${isDimmed ? 'opacity-30 scale-[0.98]' : isHovered ? 'scale-[1.02]' : 'opacity-100'}`} data-id="element-338">
                  {/* Node */}
                  <div className={`relative z-10 w-20 h-20 rounded-2xl ${layer.bg} border ${layer.border} flex items-center justify-center flex-shrink-0 transition-all duration-700 ease-premium ${isHovered ? 'shadow-[0_0_30px_rgba(219,127,74,0.2)] border-brand-orange/50 bg-brand-orange/10' : ''}`} data-id="element-339">
                    <layer.icon className={`w-8 h-8 ${layer.color} transition-colors duration-500`} data-id="element-340" />
                  </div>

                  {/* Card */}
                  <div className={`flex-1 bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 transition-all duration-700 ease-premium ${isHovered ? 'border-white/20 bg-[#111] shadow-xl' : ''} ${index > 0 && index < layers.length - 1 ? 'border-l-brand-orange/20' : ''} ${isHovered && index > 0 && index < layers.length - 1 ? 'border-l-brand-orange/60' : ''}`} data-id="element-341">
                    <h3 className="text-lg font-medium text-white mb-2 tracking-tight" data-id="element-342">
                      {layer.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed font-light" data-id="element-343">
                      {layer.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}